/**
 * Perspective + affine warping — the replacement for cv2.getPerspectiveTransform
 * / warpPerspective and getRotationMatrix2D / warpAffine.
 *
 * We solve the 3x3 homography from 4 src→4 dst correspondences (8x8 linear
 * system, Gaussian elimination), then inverse-map every destination pixel with
 * bilinear sampling. Out-of-bounds samples are filled white (255,255,255),
 * matching OpenCV's borderValue=(255,255,255).
 */
import { ImagePixels } from '../../core/ports';
import { createImage } from './image';
import { Point, orderPoints } from './geometry';

/** Solve a square linear system A·x = b in place (partial-pivot Gaussian elim). */
function solveLinear(A: number[][], b: number[]): number[] {
  const n = b.length;
  for (let col = 0; col < n; col++) {
    let piv = col;
    for (let r = col + 1; r < n; r++) {
      if (Math.abs(A[r][col]) > Math.abs(A[piv][col])) piv = r;
    }
    [A[col], A[piv]] = [A[piv], A[col]];
    [b[col], b[piv]] = [b[piv], b[col]];
    const d = A[col][col] || 1e-12;
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const f = A[r][col] / d;
      if (f === 0) continue;
      for (let c = col; c < n; c++) A[r][c] -= f * A[col][c];
      b[r] -= f * b[col];
    }
  }
  return b.map((v, i) => v / (A[i][i] || 1e-12));
}

/**
 * 3x3 homography mapping the 4 src points to the 4 dst points. Returns the
 * matrix in row-major order [h00..h22] with h22 = 1.
 */
export function getPerspectiveTransform(src: Point[], dst: Point[]): number[] {
  const A: number[][] = [];
  const b: number[] = [];
  for (let i = 0; i < 4; i++) {
    const { x, y } = src[i];
    const { x: u, y: v } = dst[i];
    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
    b.push(u);
    A.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
    b.push(v);
  }
  const h = solveLinear(A, b);
  return [h[0], h[1], h[2], h[3], h[4], h[5], h[6], h[7], 1];
}

/** Invert a 3x3 matrix (row-major). */
function invert3x3(m: number[]): number[] {
  const [a, b, c, d, e, f, g, h, i] = m;
  const A = e * i - f * h;
  const B = -(d * i - f * g);
  const C = d * h - e * g;
  const det = a * A + b * B + c * C || 1e-12;
  const id = 1 / det;
  return [
    A * id,
    (c * h - b * i) * id,
    (b * f - c * e) * id,
    B * id,
    (a * i - c * g) * id,
    (c * d - a * f) * id,
    C * id,
    (b * g - a * h) * id,
    (a * e - b * d) * id,
  ];
}

function sampleBilinearWhite(img: ImagePixels, fx: number, fy: number, out: Uint8ClampedArray, o: number): void {
  const { data, width: W, height: H } = img;
  if (fx < -1 || fy < -1 || fx > W || fy > H) {
    out[o] = out[o + 1] = out[o + 2] = 255;
    out[o + 3] = 255;
    return;
  }
  const x0 = Math.floor(fx), y0 = Math.floor(fy);
  const x1 = x0 + 1, y1 = y0 + 1;
  const wx = fx - x0, wy = fy - y0;
  const px = (x: number, y: number, c: number) => {
    if (x < 0 || y < 0 || x >= W || y >= H) return 255;
    return data[(y * W + x) * 4 + c];
  };
  for (let c = 0; c < 3; c++) {
    const top = px(x0, y0, c) * (1 - wx) + px(x1, y0, c) * wx;
    const bot = px(x0, y1, c) * (1 - wx) + px(x1, y1, c) * wx;
    out[o + c] = top * (1 - wy) + bot * wy;
  }
  out[o + 3] = 255;
}

/**
 * Warp `img` so the (ordered) src quad maps onto the WxH dst rectangle.
 * `M` is the forward homography (src→dst); we invert it to inverse-map.
 */
export function warpPerspective(img: ImagePixels, M: number[], W: number, H: number): ImagePixels {
  const out = createImage(W, H);
  const inv = invert3x3(M);
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const d = inv[6] * x + inv[7] * y + inv[8];
      const sx = (inv[0] * x + inv[1] * y + inv[2]) / d;
      const sy = (inv[3] * x + inv[4] * y + inv[5]) / d;
      sampleBilinearWhite(img, sx, sy, out.data, (y * W + x) * 4);
    }
  }
  return out;
}

/**
 * Port of docloc.four_point_warp: order the quad TL,TR,BR,BL, size the output
 * to the longer opposing sides, and perspective-warp. Returns null if the
 * computed output would be degenerate (< 10 px), matching the Python guard.
 */
export function fourPointWarp(img: ImagePixels, quad: Point[]): ImagePixels | null {
  const [tl, tr, br, bl] = orderPoints(quad);
  const wA = Math.hypot(br.x - bl.x, br.y - bl.y);
  const wB = Math.hypot(tr.x - tl.x, tr.y - tl.y);
  const hA = Math.hypot(tr.x - br.x, tr.y - br.y);
  const hB = Math.hypot(tl.x - bl.x, tl.y - bl.y);
  const W = Math.round(Math.max(wA, wB));
  const H = Math.round(Math.max(hA, hB));
  if (W < 10 || H < 10) return null;
  const dst: Point[] = [
    { x: 0, y: 0 },
    { x: W - 1, y: 0 },
    { x: W - 1, y: H - 1 },
    { x: 0, y: H - 1 },
  ];
  const M = getPerspectiveTransform([tl, tr, br, bl], dst);
  return warpPerspective(img, M, W, H);
}

/**
 * Rotate an RGBA image about a center by `angleDeg` (counter-clockwise positive,
 * matching cv2.getRotationMatrix2D) into the same WxH frame, white-filled.
 * Port of the deskew "rotate" fallback's warpAffine.
 */
export function rotateAffine(img: ImagePixels, cx: number, cy: number, angleDeg: number): ImagePixels {
  const { width: W, height: H } = img;
  const out = createImage(W, H);
  const rad = (angleDeg * Math.PI) / 180;
  // OpenCV's getRotationMatrix2D forward map; we inverse-map dst→src.
  const cos = Math.cos(rad), sin = Math.sin(rad);
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const dx = x - cx;
      const dy = y - cy;
      // inverse rotation
      const sx = cx + cos * dx + sin * dy;
      const sy = cy - sin * dx + cos * dy;
      sampleBilinearWhite(img, sx, sy, out.data, (y * W + x) * 4);
    }
  }
  return out;
}
